import { h } from "preact";
import { renderIsland } from "../../ui/renderIsland";
import { config } from "../../config";
import { nl2br } from "../../utils/string";

/**
 * One segment of a newsletter, as read out of the editor DOM.
 *
 * The editor is the source of truth (hidden inputs per segment), so these are
 * produced by `readNewsletterSegments()` rather than fetched.
 */
export type NewsletterSegment =
  | { kind: "image"; src: string }
  | { kind: "header"; text: string }
  | { kind: "text"; text: string; align: "left" | "center" | "right" }
  | { kind: "button"; text: string }
  | { kind: "largecollection"; image: string; name: string }
  | { kind: "smallcollections"; items: { image: string; name: string }[] };

/*
 * ⚠️ Admin-authored newsletter copy (header / text / button) is rendered RAW,
 * exactly as the string builder did, via `dangerouslySetInnerHTML` on the wrapper
 * div itself.
 *
 * Two deliberate details:
 *
 * 1. **Why raw.** The email that actually gets sent is rendered *outside this
 *    repo* — `newsletter.send` only queues `NewsletterQueue` rows — so whether a
 *    text or header segment may legitimately contain markup is unknowable from
 *    here. If the preview started escaping and the mailer does not, the preview
 *    would stop matching the email for any admin who types HTML. Escaping is very
 *    likely correct (copy that supported HTML would not need `nl2br`), but that is
 *    a product question, not a refactor.
 *    TODO(product): confirm the copy is plain text; if so, render `{text}`
 *    directly and delete these three escape hatches.
 *
 * 2. **Why on the div and not an inner element.** A first attempt wrapped the copy
 *    in a `<span>`, which the string version never emitted — an extra element
 *    inside `.newsletter-text` that the stylesheet could lay out differently. The
 *    conversion tests caught it. Keep the raw content on the existing div.
 */
function Segment({ segment }: { segment: NewsletterSegment }) {
  switch (segment.kind) {
    case "image":
      return (
        <div class="newsletter-image">
          <img src={segment.src} />
        </div>
      );
    case "header":
      return <div class="newsletter-header" dangerouslySetInnerHTML={{ __html: segment.text }} />;
    case "text":
      return (
        <div
          class={`newsletter-text newsletter-text-${segment.align}`}
          dangerouslySetInnerHTML={{ __html: nl2br(segment.text) }}
        />
      );
    case "button":
      return <div class="newsletter-button" dangerouslySetInnerHTML={{ __html: segment.text }} />;
    case "largecollection":
      return (
        <div class="newsletter-largecollection">
          <img src={config.imgRoot + segment.image} />
          <div class="newsletter-largecollection-title">{segment.name}</div>
          <div class="newsletter-largecollection-subtitle">Pictufy Curated Collection</div>
        </div>
      );
    case "smallcollections":
      return (
        <div class="newsletter-smallcollections">
          {segment.items.map((item, i) => (
            <div class="newsletter-smallcollections-item" key={i}>
              <img src={config.imgRoot + item.image} />
              <div class="newsletter-smallcollections-title">{item.name}</div>
            </div>
          ))}
        </div>
      );
  }
}

/**
 * The live newsletter preview.
 *
 * Replaces ~95 lines of `previewhtml +=` in `features/newsletter.ts`. Collection
 * names now escape (they come from the DB, not from the editor), which the string
 * version did not do — see the note above `Segment` for the copy fields left raw
 * on purpose.
 */
export function NewsletterPreview({ segments }: { segments: NewsletterSegment[] }) {
  return (
    <>
      {segments.map((segment, i) => (
        <Segment key={i} segment={segment} />
      ))}
    </>
  );
}

/** Re-render the preview into `#editnewsletter-preview-content`. */
export function renderNewsletterPreview(segments: NewsletterSegment[]): void {
  renderIsland("editnewsletter-preview-content", h(NewsletterPreview, { segments }));
}
